import {useCallback, useMemo, useRef} from 'react';

/**
 * Custom hook to generate common PeacockDataTable configuration objects
 * Reduces code duplication across list components
 */
export const usePeacockDataTableConfig = ({
  data,
  pageIndex,
  setPageIndex,
  setPageSize,
  setSortingObj,
  sortingObj,
  setSearchArry,
  handleResetAll,
  onDownloadBtnClick,
  showResetAll = true,
  resetAllPosition = 'left',
  disabledHeaders,
  enableSelectAllPages = true,
  onRowSelect
}) => {
  // Stable empty records reference to prevent unnecessary re-renders
  const emptyRecords = useRef([]);
  const records = data?.data ?? emptyRecords.current;

  // Handle download with row selection
  const handleDownload = useCallback(
    (rows, {isAllSelected = false, excludeIds = []} = {}) => {
      const ids = isAllSelected
        ? []
        : rows.map((r) => r.original?.id || r.original?.uuid || r.id || r.uuid);
      onDownloadBtnClick(ids, excludeIds);
    },
    [onDownloadBtnClick]
  );

  // Handle page size change with page reset
  const onRowsPerPageChange = useCallback(
    (newPageSize) => {
      setPageSize(newPageSize);
      setPageIndex(0);
    },
    [setPageSize, setPageIndex]
  );

  // Memoized configuration objects
  const paginationConfig = useMemo(
    () => ({pageIndex, setPageIndex, onRowsPerPageChange}),
    [pageIndex, setPageIndex, onRowsPerPageChange]
  );

  const sortingConfig = useMemo(
    () => ({handleSorting: setSortingObj, initialSortConfig: sortingObj}),
    [setSortingObj, sortingObj]
  );

  const filterConfig = useMemo(
    () => ({
      setSearchArry,
      onResetAll: handleResetAll,
      showResetAll,
      resetAllPosition,
      ...(disabledHeaders ? {disabledHeaders} : {})
    }),
    [
      setSearchArry,
      handleResetAll,
      showResetAll,
      resetAllPosition,
      disabledHeaders
    ]
  );

  const selectionConfig = useMemo(
    () => ({enableSelectAllPages, onRowSelect}),
    [enableSelectAllPages, onRowSelect]
  );

  return {
    records,
    handleDownload,
    paginationConfig,
    sortingConfig,
    filterConfig,
    selectionConfig
  };
};
